import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useI18n } from '../../i18n/i18n';
import { cn } from '../../lib/cn';
import { BACKDROP, POPUP } from '../ui/dialog';

/**
 * Окно гостевых форм поверх лендинга (docs/saas/screens/лендинг.md: «вход — модалка поверх
 * лендинга на маршруте /login»): вход, второй фактор, регистрация, сброс пароля, подтверждения,
 * приглашение. Открытость задаёт маршрут — закрыть окно значит уйти на лендинг.
 */
export function AuthDialog({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const { t } = useI18n();
  return (
    <BaseDialog.Root open onOpenChange={(open) => !open && onClose()}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className={BACKDROP} />
        {/* Окно входа прототипа (LoginDialog): 360 px, поля 40 px, подписи 13 px серым. */}
        <BaseDialog.Popup
          className={cn(
            POPUP,
            'max-w-[360px] overflow-y-auto rounded-xl px-8 py-10 shadow-[0_24px_64px_rgba(17,24,39,0.18)]',
            '[&_label]:text-[13px] [&_label]:font-normal [&_label]:tracking-normal [&_label]:text-muted-foreground [&_label]:normal-case',
          )}
        >
          <BaseDialog.Close
            aria-label={t('common.close')}
            className="absolute top-3 right-3 inline-flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors outline-none hover:bg-tint hover:text-foreground focus-visible:ring-1 focus-visible:ring-primary"
          >
            <X className="size-4" />
          </BaseDialog.Close>
          {children}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

/**
 * Содержимое окна: по центру название продукта и подзаголовок — что за окно («Вход»,
 * «Регистрация»), как в прототипе. Используется только внутри `AuthDialog`.
 */
export function AuthLayout({ title, children }: { title: string; children: ReactNode }) {
  const { t } = useI18n();
  return (
    <div>
      <div className="mb-7 text-center">
        <p className="mb-2 text-[26px] leading-tight font-bold tracking-[-0.02em]">
          {t('app.name')}
        </p>
        <BaseDialog.Title className="text-sm font-normal text-muted-foreground">
          {title}
        </BaseDialog.Title>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

/** Ошибка формы — строкой по центру, как в окне входа прототипа. */
export function AuthError({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="text-center text-[13px] text-danger">
      {children}
    </p>
  );
}

/** Ссылки под формой — по центру, 13 px, синим. */
export function AuthLinks({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 pt-1 text-[13px] font-medium [&_a]:text-primary [&_a]:no-underline [&_a:hover]:underline">
      {children}
    </div>
  );
}
